import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useActiveMatch } from "@/hooks/useActiveMatch";
import type { ActiveMatch } from "@/hooks/useActiveMatch";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";
import { CheckCircle, Clock, AlertTriangle, Loader2 } from "lucide-react";

function getInitials(name: string | null): string {
  if (!name) return "?";
  return name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

type MatchState = "pending" | "reported_by_you" | "reported_by_opponent" | "completed";

function getMatchState(match: ActiveMatch, userId: string): MatchState {
  if (match.status === "completed") return "completed";
  if (match.status === "reported") {
    return match.reported_by === userId ? "reported_by_you" : "reported_by_opponent";
  }
  return "pending";
}

function TeamDisplay({
  playerIds,
  profiles,
  label,
}: {
  playerIds: string[];
  profiles: ActiveMatch["profiles"];
  label: string;
}) {
  return (
    <div className="flex flex-col items-center gap-1.5">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <div className="flex items-center gap-1.5">
        {playerIds.map((pid) => {
          const profile = profiles.get(pid);
          const name = profile?.full_name ?? "Okänd";
          return (
            <div key={pid} className="flex flex-col items-center gap-0.5">
              <Avatar className="h-8 w-8">
                <AvatarFallback className="text-xs font-medium bg-primary/10 text-primary">
                  {getInitials(name)}
                </AvatarFallback>
              </Avatar>
              <span className="text-xs font-medium truncate max-w-[80px]">{name.split(" ")[0]}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ScoreDisplay({
  score1,
  score2,
  size = "lg",
}: {
  score1: number | null;
  score2: number | null;
  size?: "lg" | "sm";
}) {
  const textClass = size === "lg" ? "text-3xl" : "text-2xl";
  return (
    <div className={`flex items-center gap-3 font-bold tabular-nums ${textClass}`}>
      <span>{score1 ?? 0}</span>
      <span className="text-muted-foreground text-lg">–</span>
      <span>{score2 ?? 0}</span>
    </div>
  );
}

// Wrapper: guards on auth + active-match data, then renders the inner card
// which contains all the mutation + state hooks. Splitting this way keeps
// every hook unconditional inside the inner component (Rules of Hooks).
export default function ActiveMatchCard() {
  const { user } = useAuth();
  const { data: match, isLoading } = useActiveMatch();
  if (isLoading || !match || !user) return null;
  return <ActiveMatchCardInner match={match} userId={user.id} />;
}

function ActiveMatchCardInner({ match, userId }: { match: ActiveMatch; userId: string }) {
  const qc = useQueryClient();

  const [score1, setScore1] = useState("");
  const [score2, setScore2] = useState("");
  const [isEditing, setIsEditing] = useState(false);

  const matchState = getMatchState(match, userId);
  const pointsPerMatch = match.round.tournament.points_per_match;
  const isAutoAccept = match.round.tournament.result_confirmation === "auto";

  // Determine which team the user is on
  const userOnTeam1 = match.team1_players.includes(userId);

  async function completeMatch(
    matchId: string,
    roundId: string,
    tournamentId: string,
    scoreTeam1: number,
    scoreTeam2: number,
    team1Players: string[],
    team2Players: string[],
    _pointsPer: number,
    autoAdvance: boolean
  ) {
    // 1. Mark match completed
    const { error: updateErr } = await supabase
      .from("tournament_match_results")
      .update({ status: "completed" })
      .eq("id", matchId);
    if (updateErr) throw updateErr;

    // 2. Award points to players
    const t1Won = scoreTeam1 > scoreTeam2;
    const winnerPoints = scoreTeam1 > scoreTeam2 ? scoreTeam1 : scoreTeam2;
    const loserPoints = scoreTeam1 > scoreTeam2 ? scoreTeam2 : scoreTeam1;
    const winners = t1Won ? team1Players : team2Players;
    const losers = t1Won ? team2Players : team1Players;

    for (const pid of winners) {
      await supabase.rpc("increment_tournament_points" as any, {
        p_tournament_id: tournamentId,
        p_user_id: pid,
        p_points: winnerPoints,
      });
    }
    for (const pid of losers) {
      await supabase.rpc("increment_tournament_points" as any, {
        p_tournament_id: tournamentId,
        p_user_id: pid,
        p_points: loserPoints,
      });
    }

    // 3. Check if all round matches are completed
    const { data: roundMatches } = await supabase
      .from("tournament_match_results")
      .select("id, status")
      .eq("round_id", roundId);

    const allCompleted = (roundMatches ?? []).every(
      (m: any) => m.status === "completed"
    );

    if (allCompleted) {
      await supabase
        .from("tournament_rounds")
        .update({ status: "completed" })
        .eq("id", roundId);

      // 4. If auto_advance, invoke edge function to generate next round
      if (autoAdvance) {
        await supabase.functions.invoke("generate-tournament-round", {
          body: { tournament_id: tournamentId },
        });
      }
    }
  }

  const reportMutation = useMutation({
    mutationFn: async ({ s1, s2 }: { s1: number; s2: number }) => {
      const { error } = await supabase
        .from("tournament_match_results")
        .update({
          score_team1: s1,
          score_team2: s2,
          status: "reported",
          reported_by: userId,
        })
        .eq("id", match.id);
      if (error) throw error;

      // If auto-accept mode, complete immediately
      if (isAutoAccept) {
        await completeMatch(
          match.id,
          match.round_id,
          match.round.tournament_id,
          s1,
          s2,
          match.team1_players,
          match.team2_players,
          pointsPerMatch,
          match.round.tournament.auto_advance_rounds
        );
      }
    },
    onSuccess: () => {
      toast.success(
        isAutoAccept ? "Resultat registrerat!" : "Resultat rapporterat!"
      );
      setIsEditing(false);
      qc.invalidateQueries({ queryKey: ["active-match"] });
      qc.invalidateQueries({ queryKey: ["tournament-leaderboard"] });
    },
    onError: () => toast.error("Kunde inte rapportera resultat"),
  });

  const confirmMutation = useMutation({
    mutationFn: async () => {
      await completeMatch(
        match.id,
        match.round_id,
        match.round.tournament_id,
        match.score_team1!,
        match.score_team2!,
        match.team1_players,
        match.team2_players,
        pointsPerMatch,
        match.round.tournament.auto_advance_rounds
      );
    },
    onSuccess: () => {
      toast.success("Resultat bekräftat!");
      qc.invalidateQueries({ queryKey: ["active-match"] });
      qc.invalidateQueries({ queryKey: ["tournament-leaderboard"] });
    },
    onError: () => toast.error("Kunde inte bekräfta resultat"),
  });

  const disputeMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("tournament_match_results")
        .update({
          status: "pending",
          score_team1: null,
          score_team2: null,
          reported_by: null,
        })
        .eq("id", match.id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.info("Resultatet har återställts");
      qc.invalidateQueries({ queryKey: ["active-match"] });
    },
    onError: () => toast.error("Kunde inte ifrågasätta resultat"),
  });

  function handleReport() {
    const s1 = parseInt(score1, 10);
    const s2 = parseInt(score2, 10);
    if (isNaN(s1) || isNaN(s2) || s1 < 0 || s2 < 0) {
      toast.error("Ange giltiga poäng");
      return;
    }
    if (s1 + s2 !== pointsPerMatch) {
      toast.error(`Poängen måste summera till ${pointsPerMatch}`);
      return;
    }
    reportMutation.mutate({ s1, s2 });
  }

  const isMutating =
    reportMutation.isPending ||
    confirmMutation.isPending ||
    disputeMutation.isPending;

  return (
    <Card className="overflow-hidden rounded-2xl border shadow-sm">
      {/* Gradient header */}
      <div className="bg-gradient-to-r from-primary/90 to-primary px-4 py-3 flex items-center justify-between">
        <div>
          <p className="text-primary-foreground font-semibold text-sm">
            {match.round.tournament.name}
          </p>
          <p className="text-primary-foreground/70 text-xs">
            Omgång {match.round.round_number}
            {match.court ? ` · ${match.court.name}` : ""}
          </p>
        </div>
        <Badge
          variant="secondary"
          className="bg-white/20 text-primary-foreground border-0 text-xs"
        >
          {match.round.tournament.format}
        </Badge>
      </div>

      <CardContent className="p-4 space-y-4">
        {/* Teams */}
        <div className="flex items-center justify-between">
          <TeamDisplay
            playerIds={match.team1_players}
            profiles={match.profiles}
            label="Lag 1"
          />

          {matchState === "pending" && !isEditing ? (
            <span className="text-2xl font-bold text-muted-foreground">vs</span>
          ) : (
            <ScoreDisplay
              score1={
                matchState === "pending"
                  ? parseInt(score1) || 0
                  : match.score_team1
              }
              score2={
                matchState === "pending"
                  ? parseInt(score2) || 0
                  : match.score_team2
              }
            />
          )}

          <TeamDisplay
            playerIds={match.team2_players}
            profiles={match.profiles}
            label="Lag 2"
          />
        </div>

        {/* State-specific content */}
        {matchState === "pending" && (
          <div className="space-y-3">
            <div className="flex items-center gap-3 justify-center">
              <div className="flex flex-col items-center gap-1">
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                  Lag 1
                </label>
                <Input type="number"
                  inputMode="numeric"
                  min={0}
                  max={pointsPerMatch}
                  value={score1}
                  onChange={(e) => {
                    setScore1(e.target.value);
                    const v = parseInt(e.target.value, 10);
                    if (!isNaN(v) && v >= 0 && v <= pointsPerMatch) {
                      setScore2(String(pointsPerMatch - v));
                    }
                  }}
                  className="w-20 h-14 text-center text-2xl font-bold"
                  placeholder="0"
                />
              </div>
              <span className="text-muted-foreground font-medium text-lg mt-5">–</span>
              <div className="flex flex-col items-center gap-1">
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                  Lag 2
                </label>
                <Input type="number"
                  inputMode="numeric"
                  min={0}
                  max={pointsPerMatch}
                  value={score2}
                  onChange={(e) => {
                    setScore2(e.target.value);
                    const v = parseInt(e.target.value, 10);
                    if (!isNaN(v) && v >= 0 && v <= pointsPerMatch) {
                      setScore1(String(pointsPerMatch - v));
                    }
                  }}
                  className="w-20 h-14 text-center text-2xl font-bold"
                  placeholder="0"
                />
              </div>
            </div>
            <p className="text-center text-xs text-muted-foreground">
              Poängen måste summera till {pointsPerMatch}
            </p>
            <Button
              className="w-full h-12 text-base font-semibold"
              onClick={handleReport}
              disabled={isMutating}
            >
              {reportMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Rapportera resultat
            </Button>
          </div>
        )}

        {matchState === "reported_by_you" && !isEditing && (
          <div className="space-y-3 text-center">
            <div className="flex items-center justify-center gap-2 text-muted-foreground">
              <Clock className="h-4 w-4" />
              <span className="text-sm">Väntar på bekräftelse...</span>
            </div>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => {
                setScore1(String(match.score_team1 ?? 0));
                setScore2(String(match.score_team2 ?? 0));
                setIsEditing(true);
              }}
            >
              Ändra resultat
            </Button>
          </div>
        )}

        {matchState === "reported_by_you" && isEditing && (
          <div className="space-y-3">
            <div className="flex items-center gap-3 justify-center">
              <div className="flex flex-col items-center gap-1">
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                  Lag 1
                </label>
                <Input type="number"
                  inputMode="numeric"
                  min={0}
                  max={pointsPerMatch}
                  value={score1}
                  onChange={(e) => {
                    setScore1(e.target.value);
                    const v = parseInt(e.target.value, 10);
                    if (!isNaN(v) && v >= 0 && v <= pointsPerMatch) {
                      setScore2(String(pointsPerMatch - v));
                    }
                  }}
                  className="w-20 h-14 text-center text-2xl font-bold"
                  placeholder="0"
                />
              </div>
              <span className="text-muted-foreground font-medium text-lg mt-5">–</span>
              <div className="flex flex-col items-center gap-1">
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                  Lag 2
                </label>
                <Input type="number"
                  inputMode="numeric"
                  min={0}
                  max={pointsPerMatch}
                  value={score2}
                  onChange={(e) => {
                    setScore2(e.target.value);
                    const v = parseInt(e.target.value, 10);
                    if (!isNaN(v) && v >= 0 && v <= pointsPerMatch) {
                      setScore1(String(pointsPerMatch - v));
                    }
                  }}
                  className="w-20 h-14 text-center text-2xl font-bold"
                  placeholder="0"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setIsEditing(false)}
              >
                Avbryt
              </Button>
              <Button
                className="flex-1"
                onClick={handleReport}
                disabled={isMutating}
              >
                {reportMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : null}
                Uppdatera
              </Button>
            </div>
          </div>
        )}

        {matchState === "reported_by_opponent" && (
          <div className="space-y-3">
            <p className="text-center text-sm text-muted-foreground">
              Motståndaren har rapporterat resultatet
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1 border-destructive text-destructive hover:bg-destructive/10"
                onClick={() => disputeMutation.mutate()}
                disabled={isMutating}
              >
                {disputeMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <AlertTriangle className="h-4 w-4 mr-2" />
                )}
                Stämmer inte
              </Button>
              <Button
                className="flex-1"
                onClick={() => confirmMutation.mutate()}
                disabled={isMutating}
              >
                {confirmMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <CheckCircle className="h-4 w-4 mr-2" />
                )}
                Bekräfta
              </Button>
            </div>
          </div>
        )}

        {matchState === "completed" && (
          <div className="flex items-center justify-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-500" />
            <span className="text-sm font-medium text-green-700">
              Match avslutad
            </span>
            <Badge className="bg-green-100 text-green-700 border-0 font-semibold">
              +
              {userOnTeam1
                ? match.score_team1 ?? 0
                : match.score_team2 ?? 0}{" "}
              poäng
            </Badge>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
